import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { scrollToId, IMAGES, buildTelLink } from "@/lib/site";
import { useContent } from "@/context/ContentContext";
import { EASE } from "./Reveal";

export default function Hero() {
  const c = useContent();
  const t = c.texts;
  const tel = buildTelLink(c.settings);
  const lines = [
    { text: t.hero_title_1, cls: "text-zinc-100" },
    { text: t.hero_title_2, cls: "text-blue-400" },
  ];
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 140]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.3]);

  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-[88vh] flex items-end overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 -bottom-40" aria-hidden="true">
        <motion.img
          src={IMAGES.hero}
          alt=""
          fetchPriority="high"
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.2, ease: EASE }}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-[#09090B]/60 to-[#09090B]/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#09090B] via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8 pt-28 pb-14 sm:pb-16">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="font-mono text-xs uppercase tracking-[0.25em] font-semibold text-blue-400 mb-5"
        >
          {t.hero_overline}
        </motion.p>

        <h1 className="font-display font-extrabold tracking-tight leading-[0.98] text-[clamp(2.5rem,6.5vw,4.75rem)]">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-0.5">
              <motion.span
                className={`block ${line.cls}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease: EASE }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.45, ease: EASE }}
          className="mt-5 flex flex-wrap gap-2"
        >
          {["Mobile Innenreinigung", "Service vor Ort", c.settings.area].map((chip) => (
            <span key={chip} className="inline-flex items-center rounded-full border border-zinc-700/80 bg-zinc-950/60 px-3.5 py-1.5 text-xs font-semibold text-zinc-200">
              {chip}
            </span>
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
          className="mt-5 max-w-xl text-base text-zinc-300 leading-relaxed"
        >
          {t.hero_sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
          className="mt-7 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-booking"
            onClick={() => scrollToId("termin")}
            className="group inline-flex h-12 items-center gap-2 rounded-full bg-blue-600 px-7 text-sm font-semibold text-white hover:bg-blue-500 transition-colors"
          >
            Termin anfragen
            <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
          </button>
          <button
            data-testid="hero-cta-calculator"
            onClick={() => scrollToId("preise")}
            className="inline-flex h-12 items-center gap-2 rounded-full border border-zinc-600 px-7 text-sm font-semibold text-zinc-100 hover:border-blue-500/60 hover:text-blue-400 transition-colors"
          >
            Preis berechnen
          </button>
          {tel ? (
            <a
              data-testid="hero-cta-call"
              href={tel}
              className="inline-flex h-12 items-center gap-2 px-2 text-sm font-semibold text-zinc-300 hover:text-blue-400 transition-colors"
            >
              <Phone className="h-4 w-4 text-blue-400" />
              {c.settings.phone_display}
            </a>
          ) : null}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.95 }}
          className="mt-10 flex flex-wrap gap-x-10 gap-y-3 border-t border-white/10 pt-5"
        >
          {[
            ["100 %", "mobil vor Ort"],
            ["Innenraum", "& Lederpflege"],
            c.settings.response_time
              ? ["Antwort", c.settings.response_time]
              : ["Termine", "nach Vereinbarung"],
          ].map(([big, small]) => (
            <div key={big} className="flex items-baseline gap-3">
              <span className="font-mono text-sm font-bold text-blue-400">{big}</span>
              <span className="text-sm text-zinc-400">{small}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
